import { get, sumBy } from 'lodash';

import { ALIEO_TYPE, ASSET_TYPE, EXPENSE_TYPE, FIELD_PATHS, INCOME_TYPE, LIABILITY_TYPE } from '@funnels/constants';
import {
  AnyFormValues,
  Asset,
  Company,
  Employment,
  Expense,
  Expenses,
  Income,
  Liability,
  StoreApplicant,
} from '@funnels/types';
import { getApplicantDisplayName, getSelfEmployedYearlyIncomeAmount } from '@funnels/utils';

import { Item } from '../types';

const ASSET_TYPES = Object.values(ASSET_TYPE);
const LIABILITY_TYPES = Object.values(LIABILITY_TYPE);
const INCOME_TYPES = Object.values(INCOME_TYPE);

type ALIEO = Asset | Liability | Income | Expenses;

function getAmount(alieo: ALIEO, alieoType: ALIEO_TYPE) {
  switch (alieoType) {
    case ALIEO_TYPE.ASSET:
      return get({ assets: [alieo] }, FIELD_PATHS.ASSET_ESTIMATED_VALUE(0), 0);
    case ALIEO_TYPE.LIABILITY:
      const creditLimit = get({ liabilities: [alieo] }, FIELD_PATHS.LIABILITY_CREDIT_LIMIT(0));
      const outstandingBalance = get({ liabilities: [alieo] }, FIELD_PATHS.LIABILITY_OUTSTANDING_BALANCE(0), 0);

      return creditLimit || outstandingBalance;
    case ALIEO_TYPE.INCOME:
      return get({ incomes: [alieo] }, FIELD_PATHS.INCOME_AMOUNT(0), 0);
    case ALIEO_TYPE.EXPENSE:
      return sumBy(get({ expenses: [alieo] }, FIELD_PATHS.EXPENSES_EXPENSES(0), []), (e: Expense) =>
        e.amount && e.type !== EXPENSE_TYPE.LIVING_EXPENSES_ESTIMATE ? e.amount : 0
      );
  }
}

function getOwners(alieo: ALIEO, alieoType: ALIEO_TYPE) {
  switch (alieoType) {
    case ALIEO_TYPE.ASSET:
      return get({ assets: [alieo] }, FIELD_PATHS.ASSET_OWNERS(0), []);
    case ALIEO_TYPE.LIABILITY:
      return get({ liabilities: [alieo] }, FIELD_PATHS.LIABILITY_OWNERS(0), []);
    case ALIEO_TYPE.INCOME_SELF_EMPLOYED_V2:
      return get({ companies: [alieo] }, FIELD_PATHS.COMPANY_OWNERS(0), []);
    case ALIEO_TYPE.INCOME:
      return get({ incomes: [alieo] }, FIELD_PATHS.INCOME_OWNERS(0), []);
  }
}

function getAlieoType(alieoType: string) {
  if (ASSET_TYPES.includes(alieoType as ASSET_TYPE)) {
    return ALIEO_TYPE.ASSET;
  } else if (LIABILITY_TYPES.includes(alieoType as LIABILITY_TYPE)) {
    return ALIEO_TYPE.LIABILITY;
  } else if (INCOME_TYPES.includes(alieoType as INCOME_TYPE)) {
    if (
      [INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_FULL_DOC, INCOME_TYPE.SELF_EMPLOYED_BUSINESS_INCOME_LOW_DOC].includes(
        alieoType as INCOME_TYPE
      )
    ) {
      return ALIEO_TYPE.INCOME_SELF_EMPLOYED_V2;
    }
    return ALIEO_TYPE.INCOME;
  } else {
    return ALIEO_TYPE.EXPENSE;
  }
}

export function convertApplicantsToSummaryItems(alieos: ALIEO[], data: AnyFormValues): Item[] {
  const applicants: StoreApplicant[] = get(data, FIELD_PATHS.APPLICANTS(), []);
  const applicantsHashmap = alieos.reduce(
    (acc: { [id: string]: Item }, d: ALIEO) => {
      const alieoType = getAlieoType(get(d, 'type') ?? '');
      const amount = getAmount(d, alieoType);
      const companyClientId = get({ incomes: [d] }, FIELD_PATHS.INCOME_COMPANY_CLIENT_ID(0));
      const company = get(data, FIELD_PATHS.COMPANIES(), []).find(
        (company: Company) => get({ companies: [company] }, FIELD_PATHS.COMPANY_CLIENT_ID(0)) === companyClientId
      );
      const owners = getOwners(company || d, alieoType);

      if (alieoType === ALIEO_TYPE.EXPENSE) {
        const value = amount || 0;
        const applicantId = get({ expenses: [d] }, FIELD_PATHS.EXPENSES_APPLICANT_ID(0));
        const groupedApplicantId = get({ expenses: [d] }, FIELD_PATHS.EXPENSES_GROUPED_APPLICANT_ID(0));

        if (!applicantId) {
          acc['non-applicant'].value += value;
        } else {
          const applicantIndex = applicants.findIndex((a) => a.id === applicantId);

          if (applicantIndex !== -1) {
            const firstName = getApplicantDisplayName(
              applicants[applicantIndex],
              (name) => name ?? `Applicant ${applicantIndex + 1}`
            );

            get({ applicants }, FIELD_PATHS.APPLICANT_FIRST_NAME(applicantIndex), `Applicant ${applicantIndex + 1}`);

            // only for married expense case
            let marriedFirstName;
            if (groupedApplicantId) {
              const marriedApplicantIndex = applicants.findIndex((a) => a.id === groupedApplicantId);
              marriedFirstName = getApplicantDisplayName(
                applicants[marriedApplicantIndex],
                (name) => name ?? `Applicant ${marriedApplicantIndex + 1}`
              );
            }

            if (acc[applicantId]) {
              acc[applicantId].value += value;
            } else {
              acc[applicantId] = {
                label: marriedFirstName ? `${firstName} & ${marriedFirstName}` : firstName,
                value,
                applicantIds: [applicantId, groupedApplicantId],
              };
            }
          }
        }
      } else {
        owners &&
          owners.forEach((o: any) => {
            let value = o.percent * amount || 0;

            // SE v2 incomes
            if (ALIEO_TYPE.INCOME_SELF_EMPLOYED_V2 === alieoType) {
              const employmentIndex = get(data, FIELD_PATHS.EMPLOYMENTS(), []).findIndex(
                (employment: Employment) =>
                  get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_COMPANY_CLIENT_ID(0)) === companyClientId &&
                  get({ employments: [employment] }, FIELD_PATHS.EMPLOYMENT_APPLICANT_ID(0)) === o.applicantId
              );

              if (employmentIndex === -1) {
                return acc;
              }

              value = getSelfEmployedYearlyIncomeAmount(employmentIndex, data as AnyFormValues);
            }

            if (!o.applicantId) {
              acc['non-applicant'].value += value;
            } else {
              const applicantIndex = applicants.findIndex((a) => a.id === o.applicantId);

              if (applicantIndex !== -1) {
                const firstName = getApplicantDisplayName(
                  applicants[applicantIndex],
                  (name) => name ?? `Applicant ${applicantIndex + 1}`
                );

                if (acc[o.applicantId]) {
                  acc[o.applicantId].value += value;
                } else {
                  acc[o.applicantId] = { label: firstName, value, applicantIds: [o.applicantId] };
                }
              }
            }
          });
      }
      return acc;
    },
    { 'non-applicant': { value: 0 } }
  );
  const nonApplicant = applicantsHashmap['non-applicant'];

  delete applicantsHashmap['non-applicant'];

  const values = Object.values(applicantsHashmap);

  if (nonApplicant.value > 0) {
    return [...values, nonApplicant];
  }

  return values;
}
